import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CaeData, CaeRail } from '../../cae/cae-data.service';
import { MountingRailService, RailArticle, RailGroup } from '@article/rail/mounting-rail.service';
import { FormGroupUtils } from '@shared/form-group-utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { SharedModule } from '@shared/shared.module';
import { CaeImportOptions, CaeImportOptionsService } from '@configuration/assistants/cae-assistant/services/cae-import-options.service';
import { UserSettings } from '@user/model/user-settings';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticleConstants } from '@article/constants/article-constants';
import { RouteNavigationService } from '@services/route-navigation.service';
import { CaeImportProjectService } from '@configuration/assistants/cae-assistant/services/cae-import-project.service';
import { CaeImportArticlesOnRailService } from '@configuration/assistants/cae-assistant/services/cae-import-articles-on-rail.service';
import { CaeImportAccessoryService } from '@configuration/assistants/cae-assistant/services/cae-import-accessory.service';
import { CaeImportBridgesService } from '@configuration/assistants/cae-assistant/services/cae-import-bridges.service';
import { CaeImportLabelsService } from '@configuration/assistants/cae-assistant/services/cae-import-labels.service';
import { CaeImportMountingRailService } from '@configuration/assistants/cae-assistant/services/cae-import-mounting-rail.service';
import { CaeImportErrorCollector } from '@configuration/assistants/cae-assistant/cae-import-error-collector';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { CaeImportHelperService } from '@configuration/assistants/cae-assistant/services/cae-import-helper.service';
import { TooltipDirective } from '@shared/tooltip/tooltip.directive';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { CaeRailsData } from '@configuration/assistants/cae-assistant/services/cae-rails-data';
import { MountingRailInfo } from '@article/service/article-data.service';

@Component({
  selector: 'app-csv-import-options',
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    TruncateModule,
    SharedModule,
    TooltipDirective,
    SvgIconComponent,
  ],
  templateUrl: './csv-import-options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsvImportOptionsComponent implements OnInit {

  // Input
  public csvData!: CaeData;

  private mountingRailService = inject(MountingRailService);
  public dinOrder = this.mountingRailService.getDefaultDinOrder();
  public articleViewList = toSignal(this.mountingRailService.loadMountingRailGroups(this.dinOrder));

  public getRailArticleLabel(railArticle: RailArticle) {
    return this.mountingRailService.getRailArticleLabel(railArticle);
  }

  public selectedArticle = signal<RailArticle | undefined>(undefined);

  public projectName: string = '';
  public railForm: FormGroup;
  public railLengthFormControl: FormControl;
  public startProjection: number = 20;

  public saveSettings: boolean;
  public importOptions: CaeImportOptions;

  constructor(
    formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private overlayService: OverlayService,
    private userSettings: UserSettings,
    private caeImportOptionsService: CaeImportOptionsService,
    private localeResourceManager: LocaleResourceManager,
    private caeImportHelperService: CaeImportHelperService,
    private routeNavigationService: RouteNavigationService,
    private caeImportProjectService: CaeImportProjectService,
    private caeImportArticlesOnRailService: CaeImportArticlesOnRailService,
    private caeImportAccessoryService: CaeImportAccessoryService,
    private caeImportBridgesService: CaeImportBridgesService,
    private caeImportLabelsService: CaeImportLabelsService,
    private caeImportMountingRailService: CaeImportMountingRailService,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {
    this.saveSettings = userSettings.getUserSettingBooleanValue("caeImportSaveSettings", true);
    this.importOptions = caeImportOptionsService.load();
    this.railLengthFormControl = new FormControl(500);
    this.railForm = formBuilder.group({
      railLengthFormControl: this.railLengthFormControl,
    });
    FormGroupUtils.updateFormGroupEnabled(this.selectedArticle !== null, this.railForm);
    effect(() => {
      const selectedArticle = this.selectedArticle();
      this.updateRailValidators(selectedArticle);
    });
    effect(async () => {
      const caeRails = this.csvData?.project?.rails;
      const articleViewList = this.articleViewList();
      if (caeRails && articleViewList) {
        await this.setPreselectedArticle(caeRails, articleViewList);
      }
    });
  }

  public ngOnInit(): void {
    this.projectName = this.csvData.project.name;
  }

  public showArticleTechnicalInfo(info: MountingRailInfo): void {
    if (!info) {
      return;
    }
    ArticlePreviewUtils.openShopLink(info.articleNo, this.localeResourceManager);
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async startImport(): Promise<void> {

    this.csvData.project.name = this.projectName;

    if (this.saveSettings) {
      this.userSettings.setUserSettingValue("caeImportSaveSettings", this.saveSettings);
      this.caeImportOptionsService.save(this.importOptions);
    }

    const selectedArticle = this.selectedArticle();
    const caeRails = this.csvData.project.rails;

    if (selectedArticle) {
      caeRails[0].articleNo = selectedArticle.no;
      caeRails[0].size.width = this.railLengthFormControl.value;
    } else {
      caeRails[0].articleNo = ArticleConstants.FREE_MOUNTING_ARTICLE_NO;
      caeRails[0].size.width = NaN;
    }

    this.close();
    const caeRailsData: CaeRailsData = {
      currentRails: caeRails,
      allRails: caeRails,
    };
    const caeImportErrorCollector = new CaeImportErrorCollector();
    this.caeImportProjectService.storeMountingInfos(caeRails[0]);
    await this.overlayService.doWithOverlay(async () => {
      await this.caeImportProjectService.createProject(this.csvData, caeRailsData);
      await this.caeImportArticlesOnRailService.addArticles(caeRailsData, this.importOptions, caeImportErrorCollector);
      await this.caeImportAccessoryService.addAccessory(caeRailsData, caeImportErrorCollector);
      await this.caeImportBridgesService.addBridges(caeRailsData, caeImportErrorCollector);
      await this.caeImportLabelsService.addLabels(caeRailsData, this.importOptions, caeImportErrorCollector);
      await this.caeImportMountingRailService.addMountingRail(caeRailsData, caeImportErrorCollector);
    }, OverlayBackground.color);
    await this.routeNavigationService.toConfigurator();
    if (caeImportErrorCollector.errors.length > 0) {
      this.projectComponentPresenter.openCsvResult(caeImportErrorCollector);
    }
  }

  private updateRailValidators(selectedArticle: RailArticle | undefined): void {
    this.railLengthFormControl.setValidators(this.caeImportHelperService.createRaillengthValidator(selectedArticle));
    FormGroupUtils.updateFormGroupEnabled(!!selectedArticle, this.railForm);
  }

  private async setPreselectedArticle(caeRails: CaeRail[], articleViewList: RailGroup[]): Promise<void> {
    const preselectedArticle = await this.caeImportHelperService.getPreselectedArticle(caeRails, articleViewList);
    this.selectedArticle.set(preselectedArticle);
  }
}
